import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    forwardRef,
    HostBinding,
    Input,
    OnDestroy,
    QueryList,
} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {queryListToObservable} from 'app/core/helpers/observable';
import {randomUUIDGenerator} from 'app/core/helpers/uuid';
import {CustomFormComponent} from 'app/shared/classes/custom-form-component';
import {InlineSelectorItemComponent} from 'app/shared/components/inline-selector-item/inline-selector-item.component';
import {Observable, Subscription} from 'rxjs';

@Component({
    selector: 'inline-selector',
    templateUrl: './inline-selector.component.html',
    styleUrls: ['./inline-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InlineSelectorComponent),
            multi: true,
        },
    ],
})
export class InlineSelectorComponent extends CustomFormComponent<any> implements AfterContentInit, OnDestroy {
    @ContentChildren(InlineSelectorItemComponent) children: QueryList<InlineSelectorItemComponent>;
    @HostBinding('class.with-image') withImage = false;
    @Input() multiSelect = false;
    name = randomUUIDGenerator();
    items$: Observable<Array<InlineSelectorItemComponent>>;
    private itemsSubscription = Subscription.EMPTY;

    get selected() {
        return this.internalValue;
    }

    constructor(el: ElementRef<HTMLElement>, cd: ChangeDetectorRef) {
        super(el, cd);
    }

    ngAfterContentInit() {
        this.items$ = queryListToObservable(this.children);
        this.itemsSubscription = this.items$.subscribe(items => {
            this.withImage = items.some(item => !!item.image);
            setTimeout(() => {
                if (this.selected !== null) {
                    this.select(this.selected);
                }
            });
        });
    }

    concatArrayValueWithSelected(value: any) {
        if (value === null) {
            return [];
        }
        const val = value || [];
        return this.multiSelect ? [].concat(this.selected || [], val) : [].concat(val);
    }

    writeValue(value: any) {
        if (Array.isArray(this.selected) || Array.isArray(value)) {
            const newValue = this.concatArrayValueWithSelected(value);
            super.writeValue(newValue);
        } else {
            super.writeValue(value);
        }
        this.select(this.selected);
    }

    ngOnDestroy() {
        this.itemsSubscription.unsubscribe();
    }

    trackByValue(_, item: InlineSelectorItemComponent) {
        return item.value;
    }

    onChecked(event: Event, item: InlineSelectorItemComponent) {
        let target = event.target as HTMLInputElement;
        if (target.checked) {
            if (Array.isArray(this.selected)) {
                const newValue = this.concatArrayValueWithSelected(item.value);
                this.select(newValue);
            } else {
                this.select(item.value);
            }
        } else if (this.multiSelect) {
            const newValue = this.selected.filter(select => select !== item.value);
            this.select(newValue);
        }
    }

    isSelected(value: any) {
        if (Array.isArray(this.selected)) {
            if (!this.selected.length) {
                return value === null;
            }
            return this.selected.includes(value);
        } else {
            return this.selected === value;
        }
    }

    private select(value: any) {
        this.invokeChangeCallback(value);
    }
}
